import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { IDR } from "@/lib/format";
import { AppHeader } from "@/components/AppHeader";
import { inputClass, btnGhost } from "@/components/Brand";

export default function Analytics() {
  const { isOwner } = useAuth();
  const { t } = useI18n();
  const [period, setPeriod] = useState("month");
  const [range, setRange] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (p, r) => {
    setLoading(true);
    try {
      const params = { period: p };
      if (p === "custom" && r?.start && r?.end) {
        params.start = r.start;
        params.end = r.end;
      }
      const res = await api.get("/analytics", { params });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load("month");
  }, [load]);

  const periods = [
    ["week", t("period_week")],
    ["month", t("period_month")],
    ["year", t("period_year")],
    ["custom", t("period_custom")],
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-[#F3F3F1] grain" data-testid="analytics-page">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-5 lg:px-12 py-12 lg:py-16 space-y-10">
        <div>
          <p className="eyebrow mb-3">{t("analytics")}</p>
          <h1 className="text-4xl sm:text-5xl tracking-tighter font-medium">{t("dashboard")}</h1>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          {periods.map(([key, label]) => (
            <button key={key} data-testid={`period-${key}`}
              onClick={() => { setPeriod(key); if (key !== "custom") load(key); }}
              className={`px-4 py-2.5 min-h-[44px] sm:min-h-0 text-[10px] tracking-[0.18em] uppercase border transition-colors ${
                period === key ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2A2A2A] hover:border-[#D4AF37]"
              }`}>
              {label}
            </button>
          ))}
          {period === "custom" && (
            <div className="flex flex-wrap items-end gap-3">
              <input data-testid="range-start" type="date" className={`${inputClass} w-40`} value={range.start}
                onChange={(e) => setRange({ ...range, start: e.target.value })} />
              <input data-testid="range-end" type="date" className={`${inputClass} w-40`} value={range.end}
                onChange={(e) => setRange({ ...range, end: e.target.value })} />
              <button data-testid="apply-range-btn" onClick={() => load("custom", range)} className={btnGhost}>
                {t("apply")}
              </button>
            </div>
          )}
        </div>

        {loading || !data ? (
          <div className="flex justify-center py-24 text-[#707070]"><Loader2 className="animate-spin" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#2A2A2A] border border-[#2A2A2A]" data-testid="analytics-cards">
              <Card label={t("projects")} value={data.project_count} testId="metric-projects" />
              <Card label={t("edits")} value={data.edits.total} sub={`+${data.edits.extra} ${t("extra")}`} testId="metric-edits" />
              <Card label={t("digital_only")} value={data.edits.digital_only} testId="metric-digital" />
              <Card label={t("for_print")} value={data.edits.for_print} testId="metric-for-print" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-7" data-testid="print-breakdown-panel">
                <p className="eyebrow mb-6">{t("prints")} · {t("frames")}</p>
                {data.print_breakdown.length === 0 && <p className="text-sm text-[#A8A8A8]">—</p>}
                {data.print_breakdown.map((b) => (
                  <div key={b.size} className="flex items-center justify-between py-3 border-b border-[#1E1E1E]">
                    <span className="text-sm">{b.size}</span>
                    <span className="font-mono-accent text-xs">
                      {b.qty} pcs {b.framed > 0 && <span className="text-[#D4AF37]">· {b.framed} {t("frames")}</span>}
                    </span>
                  </div>
                ))}
                <p className="mt-5 font-mono-accent text-[11px] text-[#707070]">
                  {t("frames")}: {data.frames_total}
                </p>
              </div>

              <div className="space-y-6">
                <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-7" data-testid="pipeline-panel">
                  <p className="eyebrow mb-6">{t("pipeline")}</p>
                  <div className="grid grid-cols-3 gap-3 sm:gap-4">
                    <Mini label="Editing" value={data.pipeline.editing} />
                    <Mini label="Printing" value={data.pipeline.printing} />
                    <Mini label="Delivered" value={data.pipeline.delivered} />
                  </div>
                </div>

                <div className={`border p-7 ${data.revenue ? "border-[#D4AF37]/40 bg-[#0F0F0F]" : "border-[#2A2A2A] bg-[#0B0B0B]"}`} data-testid="revenue-panel">
                  <p className="eyebrow mb-6">{t("revenue")}</p>
                  {data.revenue ? (
                    <>
                      <p data-testid="revenue-total" className="font-mono-accent text-3xl text-[#D4AF37]">{IDR(data.revenue.total)}</p>
                      <p className="mt-3 text-xs text-[#707070]">{t("avg_per_project")}: {IDR(data.revenue.average_per_project)}</p>
                    </>
                  ) : (
                    <p className="flex items-center gap-2 text-sm text-[#A8A8A8]" data-testid="revenue-locked">
                      <Lock size={14} /> {t("revenue_hidden")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const Card = ({ label, value, sub, testId }) => (
  <div className="bg-[#0B0B0B] p-7" data-testid={testId}>
    <p className="text-[10px] tracking-[0.18em] uppercase text-[#707070]">{label}</p>
    <p className="mt-3 font-mono-accent text-2xl">{value}</p>
    {sub && <p className="mt-1 text-[11px] text-[#D4AF37]">{sub}</p>}
  </div>
);

const Mini = ({ label, value }) => (
  <div>
    <p className="text-[10px] tracking-[0.18em] uppercase text-[#707070]">{label}</p>
    <p className="mt-1.5 font-mono-accent text-lg">{value}</p>
  </div>
);
